import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Play, Square, Plus, Trash2, Save, FolderOpen, Download, Settings, Terminal, AlertTriangle, Package } from 'lucide-react';

// Global Pyodide declaration
declare global {
  interface Window {
    loadPyodide?: (config: any) => Promise<any>;
  }
}

// Simplified Pyodide interface for our use case
interface PyodideInterface {
  runPython: (code: string) => any;
  loadPackage: (packages: string | string[]) => Promise<void>;
  globals: any;
}

interface NotebookCell {
  id: string;
  type: 'code' | 'markdown';
  content: string;
  output?: string;
  executionCount?: number;
  isRunning?: boolean;
  error?: string;
}

// Agent interaction interface for notebook
export interface NotebookAgent {
  executeCell: (cellIndex: number) => Promise<boolean>;
  addCell: (type: 'code' | 'markdown', content?: string) => Promise<string>;
  deleteCell: (cellIndex: number) => Promise<boolean>;
  getCells: () => NotebookCell[];
  runPython: (code: string) => Promise<string>;
  installPackage: (packageName: string) => Promise<boolean>;
  clearOutputs: () => void;
  saveNotebook: () => Promise<string>;
}

const JupyterNotebookTab: React.FC = () => {
  const [cells, setCells] = useState<NotebookCell[]>([
    {
      id: '1',
      type: 'code',
      content: '# Welcome to Browser-based Python Notebook\nprint("Hello, World!")\nimport sys\nprint(f"Python version: {sys.version}")'
    }
  ]);
  
  const [pyodide, setPyodide] = useState<PyodideInterface | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState('Initializing Python environment...');
  const [globalExecutionCount, setGlobalExecutionCount] = useState(1);
  const [isAgentMode, setIsAgentMode] = useState(false);
  const [agentLogs, setAgentLogs] = useState<string[]>([]);
  const [packageName, setPackageName] = useState('');
  const [isInstallingPackage, setIsInstallingPackage] = useState(false);
  const [installedPackages, setInstalledPackages] = useState<string[]>(['micropip']);
  
  const workerRef = useRef<Worker | null>(null);
  const outputBufferRef = useRef<{ [cellId: string]: string }>({});

  // Initialize Pyodide
  useEffect(() => {
    let mounted = true;

    const initPyodide = async () => {
      try {
        setLoadingStatus('Loading Pyodide (Python WebAssembly)...');
        
        setLoadingStatus('Loading Pyodide from CDN...');
        
        // Check if Pyodide is already loaded
        let pyodideInstance;
        if (window.loadPyodide) {
          setLoadingStatus('Initializing Python interpreter...');
          pyodideInstance = await (window.loadPyodide as (config: any) => Promise<any>)({
            indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.28.2/full/',
            stdout: () => {},
            stderr: (text: string) => console.error('Python stderr:', text)
          });
        } else {
          // Load Pyodide script dynamically
          const existingScript = document.querySelector('script[src*="pyodide.js"]');
          if (!existingScript) {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/pyodide/v0.28.2/full/pyodide.js';
            
            const pyodideLoaded = new Promise<any>((resolve, reject) => {
              script.onload = () => {
                setLoadingStatus('Initializing Python interpreter...');
                if (window.loadPyodide) {
                  (window.loadPyodide as (config: any) => Promise<any>)({
                    indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.28.2/full/',
                    stdout: () => {},
                    stderr: (text: string) => console.error('Python stderr:', text)
                  }).then(resolve).catch(reject);
                } else {
                  reject(new Error('Pyodide failed to load'));
                }
              };
              script.onerror = reject;
              document.head.appendChild(script);
            });
            
            pyodideInstance = await pyodideLoaded;
          } else {
            // Wait for existing script to load
            while (!window.loadPyodide) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
            setLoadingStatus('Initializing Python interpreter...');
            if (window.loadPyodide) {
              pyodideInstance = await (window.loadPyodide as (config: any) => Promise<any>)({
                indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.28.2/full/',
                stdout: () => {},
                stderr: (text: string) => console.error('Python stderr:', text)
              });
            } else {
              throw new Error('Pyodide failed to load');
            }
          }
        }

        if (!mounted) return;

        setLoadingStatus('Loading essential packages...');
        
        // Load basic packages (start with smaller ones for faster load)
        try {
          setLoadingStatus('Loading Python packages...');
          await pyodideInstance.loadPackage(['micropip']);
          addAgentLog('Pre-loaded packages: micropip');
          setInstalledPackages(['micropip']);
        } catch (error) {
          console.warn('Failed to preload packages:', error);
          addAgentLog('Warning: Some packages may not be available');
        }

        // Set up Python environment with matplotlib support
        pyodideInstance.runPython(`
import sys
import io
from contextlib import redirect_stdout, redirect_stderr
import traceback
import builtins
import base64

# Global output capture
_output_buffer = []
_error_buffer = []
_figure_buffer = []

# Store original print function
_original_print = builtins.print

def capture_print(*args, **kwargs):
    # Capture to our buffer
    _output_buffer.append(' '.join(str(arg) for arg in args))

def get_output():
    text_output = '\\n'.join(_output_buffer)
    figure_output = '\\n'.join(_figure_buffer)
    if figure_output:
        return text_output + ('\\n' if text_output else '') + figure_output
    return text_output

def clear_output():
    global _output_buffer, _error_buffer, _figure_buffer
    _output_buffer = []
    _error_buffer = []
    _figure_buffer = []

def setup_matplotlib():
    """Configure matplotlib for inline display in browser"""
    try:
        import matplotlib
        matplotlib.use('AGG')  # Use Anti-Grain Geometry backend for browser
        import matplotlib.pyplot as plt
        
        # Monkey-patch plt.show() to capture figures
        original_show = plt.show
        
        def inline_show(*args, **kwargs):
            import matplotlib.pyplot as plt
            fig = plt.gcf()
            buf = io.BytesIO()
            fig.savefig(buf, format='png', bbox_inches='tight', dpi=100)
            buf.seek(0)
            img_data = base64.b64encode(buf.read()).decode('utf-8')
            _figure_buffer.append(f'<img src="data:image/png;base64,{img_data}" style="max-width: 100%; height: auto;" />')
            plt.clf()  # Clear the figure after saving
            plt.close('all')  # Close all figures to free memory
        
        plt.show = inline_show
        return True
    except ImportError:
        return False

def safe_exec(code):
    clear_output()
    try:
        # Temporarily replace print function
        builtins.print = capture_print
        
        # Create a namespace with matplotlib configured
        exec_globals = {'__builtins__': __builtins__}
        exec_locals = {}
        
        # Check if matplotlib is being imported and set it up
        if 'matplotlib' in code or 'pyplot' in code or 'plt' in code:
            setup_matplotlib()
        
        # Compile first to catch syntax errors
        compiled = compile(code, '<notebook>', 'exec')
        exec(compiled, exec_globals, exec_locals)
        
        # If matplotlib was used, ensure any pending figures are captured
        try:
            import matplotlib.pyplot as plt
            if plt.get_fignums():  # Check if there are any open figures
                for fig_num in plt.get_fignums():
                    fig = plt.figure(fig_num)
                    buf = io.BytesIO()
                    fig.savefig(buf, format='png', bbox_inches='tight', dpi=100)
                    buf.seek(0)
                    img_data = base64.b64encode(buf.read()).decode('utf-8')
                    _figure_buffer.append(f'<img src="data:image/png;base64,{img_data}" style="max-width: 100%; height: auto;" />')
                plt.close('all')
        except:
            pass
        
        # Restore original print
        builtins.print = _original_print
        
        return get_output(), None
    except Exception as e:
        # Restore original print on error
        builtins.print = _original_print
        error_msg = traceback.format_exc()
        return get_output(), error_msg
        `);

        // Create our simplified interface
        const simplifiedPyodide: PyodideInterface = {
          runPython: pyodideInstance.runPython.bind(pyodideInstance),
          loadPackage: pyodideInstance.loadPackage.bind(pyodideInstance),
          globals: pyodideInstance.globals
        };
        
        setPyodide(simplifiedPyodide);
        setIsLoading(false);
        setLoadingStatus('');
        addAgentLog('Jupyter notebook environment initialized');

      } catch (error) {
        console.error('Failed to initialize Pyodide:', error);
        setLoadingStatus(`Error: ${error}`);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initPyodide();

    return () => {
      mounted = false;
    };
  }, []);

  const addAgentLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setAgentLogs(prev => [...prev.slice(-19), `[${timestamp}] ${message}`]);
  }, []);

  // Execute Python code with output capture
  const executePython = useCallback(async (code: string): Promise<{ output: string; error?: string }> => {
    if (!pyodide) {
      return { output: '', error: 'Python environment not ready' };
    }

    try {
      const result = pyodide.runPython(`safe_exec(${JSON.stringify(code)})`);
      // Handle result conversion safely
      let output, error;
      try {
        if (result && typeof result.toJs === 'function') {
          [output, error] = result.toJs();
        } else {
          [output, error] = result;
        }
      } catch {
        [output, error] = [result, null];
      }
      
      return {
        output: output || '',
        error: error || undefined
      };
    } catch (error) {
      return {
        output: '',
        error: `Execution error: ${error}`
      };
    }
  }, [pyodide]);

  // Package management function
  const installPackage = useCallback(async (packageName: string) => {
    if (!pyodide || !packageName.trim()) return false;
    
    setIsInstallingPackage(true);
    addAgentLog(`Installing package: ${packageName}`);
    
    try {
      // Try using micropip first (for pure Python packages)
      const result = await executePython(`
import micropip
await micropip.install('${packageName}')
print(f'Successfully installed {packageName}')
      `);
      
      if (result.error) {
        // If micropip fails, try pyodide.loadPackage for built-in packages
        await pyodide.loadPackage(packageName);
        addAgentLog(`Package ${packageName} installed via pyodide`);
      } else {
        addAgentLog(`Package ${packageName} installed via micropip`);
      }
      
      setInstalledPackages(prev => [...prev, packageName]);
      return true;
    } catch (error) {
      addAgentLog(`Failed to install ${packageName}: ${error}`);
      return false;
    } finally {
      setIsInstallingPackage(false);
    }
  }, [pyodide, executePython, addAgentLog]);

  // Cell management functions
  const addCell = useCallback((type: 'code' | 'markdown' = 'code', afterIndex?: number) => {
    const newCell: NotebookCell = {
      id: Date.now().toString(),
      type,
      content: type === 'code' ? '' : '# New Markdown Cell\n\nWrite your markdown here...'
    };

    setCells(prev => {
      if (afterIndex !== undefined) {
        const newCells = [...prev];
        newCells.splice(afterIndex + 1, 0, newCell);
        return newCells;
      }
      return [...prev, newCell];
    });

    return newCell.id;
  }, []);

  const deleteCell = useCallback((cellId: string) => {
    setCells(prev => prev.filter(cell => cell.id !== cellId));
  }, []);

  const updateCell = useCallback((cellId: string, content: string) => {
    setCells(prev => prev.map(cell => 
      cell.id === cellId ? { ...cell, content } : cell
    ));
  }, []);

  const executeCell = useCallback(async (cellId: string) => {
    const cell = cells.find(c => c.id === cellId);
    if (!cell || cell.type !== 'code' || !pyodide) return;

    // Mark cell as running
    setCells(prev => prev.map(c => 
      c.id === cellId 
        ? { ...c, isRunning: true, error: undefined }
        : c
    ));

    addAgentLog(`Executing cell: ${cell.content.substring(0, 50)}...`);

    try {
      const { output, error } = await executePython(cell.content);
      
      setCells(prev => prev.map(c => 
        c.id === cellId 
          ? { 
              ...c, 
              isRunning: false,
              output: output,
              error: error,
              executionCount: globalExecutionCount
            }
          : c
      ));

      setGlobalExecutionCount(prev => prev + 1);
      addAgentLog(`Cell executed successfully`);

    } catch (error) {
      setCells(prev => prev.map(c => 
        c.id === cellId 
          ? { 
              ...c, 
              isRunning: false,
              error: `Execution failed: ${error}`,
              executionCount: globalExecutionCount
            }
          : c
      ));
      setGlobalExecutionCount(prev => prev + 1);
      addAgentLog(`Cell execution failed: ${error}`);
    }
  }, [cells, pyodide, executePython, globalExecutionCount, addAgentLog]);

  // Agent API
  const notebookAgent: NotebookAgent = {
    executeCell: async (cellIndex: number): Promise<boolean> => {
      if (cellIndex < 0 || cellIndex >= cells.length) return false;
      const cell = cells[cellIndex];
      await executeCell(cell.id);
      return true;
    },

    addCell: async (type: 'code' | 'markdown', content?: string): Promise<string> => {
      const cellId = addCell(type);
      if (content) {
        setTimeout(() => updateCell(cellId, content), 10);
      }
      addAgentLog(`Added ${type} cell`);
      return cellId;
    },

    deleteCell: async (cellIndex: number): Promise<boolean> => {
      if (cellIndex < 0 || cellIndex >= cells.length) return false;
      const cell = cells[cellIndex];
      deleteCell(cell.id);
      addAgentLog(`Deleted cell at index ${cellIndex}`);
      return true;
    },

    getCells: (): NotebookCell[] => cells,

    runPython: async (code: string): Promise<string> => {
      addAgentLog(`Executing Python: ${code.substring(0, 50)}...`);
      const { output, error } = await executePython(code);
      return error ? `Error: ${error}` : output;
    },

    installPackage: async (packageName: string): Promise<boolean> => {
      return await installPackage(packageName);
    },

    clearOutputs: (): void => {
      setCells(prev => prev.map(cell => ({
        ...cell,
        output: undefined,
        error: undefined,
        executionCount: undefined
      })));
      addAgentLog('Cleared all outputs');
    },

    saveNotebook: async (): Promise<string> => {
      const notebook = {
        cells: cells.map(cell => ({
          cell_type: cell.type,
          source: cell.content.split('\n'),
          outputs: cell.output ? [{ output_type: 'stream', text: cell.output.split('\n') }] : [],
          execution_count: cell.executionCount || null
        }))
      };
      
      const jsonStr = JSON.stringify(notebook, null, 2);
      addAgentLog('Notebook exported to JSON');
      return jsonStr;
    }
  };

  // Expose agent globally
  useEffect(() => {
    (window as any).notebookAgent = notebookAgent;
    return () => {
      if ((window as any).notebookAgent) {
        delete (window as any).notebookAgent;
      }
    };
  }, [cells, pyodide, executePython, addAgentLog, addCell, deleteCell, updateCell, executeCell, globalExecutionCount]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-engramiq-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-engramiq-light-grey font-medium mb-2">Initializing Python Environment</div>
          <div className="text-engramiq-bright-grey text-sm">{loadingStatus}</div>
          <div className="text-engramiq-bright-grey text-xs mt-2">This may take 30-60 seconds on first load</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-engramiq-dark-blue flex flex-col">
      {/* Toolbar */}
      <div className="bg-engramiq-dark-blue border-b border-engramiq-green/20 p-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={() => addCell('code')}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-engramiq-green/20 text-engramiq-green rounded hover:bg-engramiq-green/30"
            title="Add Code Cell"
          >
            <Plus className="w-3 h-3" />
            Code
          </button>
          
          <button
            onClick={() => addCell('markdown')}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-engramiq-green/20 text-engramiq-green rounded hover:bg-engramiq-green/30"
            title="Add Markdown Cell"
          >
            <Plus className="w-3 h-3" />
            Markdown
          </button>

          <div className="w-px h-4 bg-engramiq-green/20 mx-2"></div>

          <button
            onClick={() => setCells(prev => prev.map(cell => ({ ...cell, output: undefined, error: undefined })))}
            className="flex items-center gap-1 px-2 py-1 text-xs text-engramiq-light-grey hover:text-engramiq-green"
            title="Clear All Outputs"
          >
            <Square className="w-3 h-3" />
            Clear
          </button>

          <button
            onClick={() => setIsAgentMode(!isAgentMode)}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded ${
              isAgentMode ? 'bg-engramiq-green/20 text-engramiq-green' : 'text-engramiq-light-grey hover:text-engramiq-green'
            }`}
            title="Toggle Agent Mode"
          >
            <Terminal className="w-3 h-3" />
            Agent
          </button>

          <div className="w-px h-4 bg-engramiq-green/20 mx-2"></div>

          {/* Package Installation */}
          <div className="flex items-center gap-1">
            <Package className="w-3 h-3 text-engramiq-light-grey" />
            <input
              type="text"
              value={packageName}
              onChange={(e) => setPackageName(e.target.value)}
              placeholder="Package name..."
              className="w-24 px-1 py-0.5 text-xs bg-black/20 border border-engramiq-green/20 rounded text-engramiq-light-grey placeholder-engramiq-bright-grey focus:outline-none focus:border-engramiq-green"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && packageName.trim()) {
                  installPackage(packageName.trim());
                  setPackageName('');
                }
              }}
            />
            <button
              onClick={() => {
                if (packageName.trim()) {
                  installPackage(packageName.trim());
                  setPackageName('');
                }
              }}
              disabled={!packageName.trim() || isInstallingPackage}
              className="px-1 py-0.5 text-xs bg-engramiq-green/20 text-engramiq-green rounded hover:bg-engramiq-green/30 disabled:opacity-50"
              title="Install Package"
            >
              {isInstallingPackage ? '...' : 'Install'}
            </button>
          </div>

          <div className="flex-1"></div>

          <div className="text-xs text-engramiq-bright-grey">
            Python {pyodide ? '3.12' : 'Loading...'}
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Notebook Area */}
        <div className={`${isAgentMode ? 'flex-1' : 'w-full'} flex flex-col`}>
          <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            {cells.map((cell, index) => (
              <NotebookCell
                key={cell.id}
                cell={cell}
                index={index}
                onUpdate={(content) => updateCell(cell.id, content)}
                onExecute={() => executeCell(cell.id)}
                onDelete={() => deleteCell(cell.id)}
                onAddAfter={() => addCell('code', index)}
              />
            ))}
          </div>
        </div>

        {/* Agent Panel */}
        {isAgentMode && (
          <div className="w-80 bg-engramiq-dark-blue border-l border-engramiq-green/20 flex flex-col">
            <div className="p-3 border-b border-engramiq-green/20">
              <h3 className="text-sm font-bold text-engramiq-light-grey mb-2">Notebook Agent</h3>
              <div className="text-xs text-engramiq-bright-grey">
                Agent available at <code className="text-engramiq-green">window.notebookAgent</code>
              </div>
            </div>
            
            <div className="flex-1 overflow-hidden">
              <div className="p-3">
                <h4 className="text-xs font-semibold text-engramiq-green mb-2">Agent Logs</h4>
                <div className="h-32 bg-black/20 border border-engramiq-green/20 rounded p-2 overflow-y-auto">
                  {agentLogs.length === 0 ? (
                    <div className="text-xs text-engramiq-bright-grey">No agent activity yet</div>
                  ) : (
                    agentLogs.map((log, index) => (
                      <div key={index} className="text-xs text-engramiq-light-grey mb-1 font-mono">
                        {log}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="p-3 border-t border-engramiq-green/20">
                <h4 className="text-xs font-semibold text-engramiq-green mb-2">Agent Methods</h4>
                <div className="text-xs text-engramiq-bright-grey space-y-1">
                  <div><code>executeCell(index)</code></div>
                  <div><code>addCell(type, content)</code></div>
                  <div><code>deleteCell(index)</code></div>
                  <div><code>runPython(code)</code></div>
                  <div><code>installPackage(name)</code></div>
                  <div><code>getCells()</code></div>
                  <div><code>clearOutputs()</code></div>
                  <div><code>saveNotebook()</code></div>
                </div>
              </div>

              <div className="p-3 border-t border-engramiq-green/20">
                <h4 className="text-xs font-semibold text-engramiq-green mb-2">Installed Packages</h4>
                <div className="h-20 bg-black/20 border border-engramiq-green/20 rounded p-2 overflow-y-auto">
                  {installedPackages.length === 0 ? (
                    <div className="text-xs text-engramiq-bright-grey">No packages installed</div>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {installedPackages.map((pkg, index) => (
                        <span key={index} className="px-1 py-0.5 bg-engramiq-green/20 text-engramiq-green rounded text-xs">
                          {pkg}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-3 border-t border-engramiq-green/20">
                <h4 className="text-xs font-semibold text-engramiq-green mb-2">Sandbox Info</h4>
                <div className="text-xs text-engramiq-bright-grey space-y-1">
                  <div>✅ File system isolated</div>
                  <div>✅ No network access</div>
                  <div>✅ Memory limited</div>
                  <div>✅ WebAssembly sandbox</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Individual notebook cell component
interface NotebookCellProps {
  cell: NotebookCell;
  index: number;
  onUpdate: (content: string) => void;
  onExecute: () => void;
  onDelete: () => void;
  onAddAfter: () => void;
}

const NotebookCell: React.FC<NotebookCellProps> = ({
  cell,
  index,
  onUpdate,
  onExecute,
  onDelete,
  onAddAfter
}) => {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <div className="border border-engramiq-green/20 rounded-lg bg-engramiq-dark-blue/50">
      {/* Cell Header */}
      <div className="flex items-center justify-between p-2 border-b border-engramiq-green/20">
        <div className="flex items-center gap-2">
          <span className="text-xs text-engramiq-bright-grey">
            {cell.type === 'code' ? `[${cell.executionCount || ' '}]:` : 'Markdown'}
          </span>
          {cell.isRunning && (
            <div className="w-3 h-3 border border-engramiq-green border-t-transparent rounded-full animate-spin"></div>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          {cell.type === 'code' && (
            <button
              onClick={onExecute}
              disabled={cell.isRunning}
              className="p-1 text-engramiq-green hover:bg-engramiq-green/20 rounded disabled:opacity-50"
              title="Run Cell"
            >
              <Play className="w-3 h-3" />
            </button>
          )}
          
          <button
            onClick={onAddAfter}
            className="p-1 text-engramiq-light-grey hover:bg-engramiq-green/20 rounded"
            title="Add Cell Below"
          >
            <Plus className="w-3 h-3" />
          </button>
          
          <button
            onClick={onDelete}
            className="p-1 text-accent-red hover:bg-accent-red/20 rounded"
            title="Delete Cell"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Cell Content */}
      <div className="p-3">
        <textarea
          value={cell.content}
          onChange={(e) => onUpdate(e.target.value)}
          className="w-full bg-black/20 border border-engramiq-green/20 rounded p-2 text-engramiq-light-grey font-mono text-sm resize-none focus:outline-none focus:border-engramiq-green"
          rows={Math.max(3, cell.content.split('\n').length)}
          placeholder={cell.type === 'code' ? '# Enter Python code...' : '# Markdown content...'}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && cell.type === 'code') {
              e.preventDefault();
              onExecute();
            }
          }}
        />
      </div>

      {/* Output */}
      {(cell.output || cell.error) && (
        <div className="border-t border-engramiq-green/20 p-3">
          {cell.error ? (
            <div className="bg-accent-red/20 border border-accent-red/50 rounded p-2">
              <div className="flex items-center gap-1 text-accent-red text-xs mb-1">
                <AlertTriangle className="w-3 h-3" />
                Error
              </div>
              <pre className="text-xs text-accent-red whitespace-pre-wrap overflow-x-auto">
                {cell.error}
              </pre>
            </div>
          ) : cell.output && (
            <div className="bg-green-900/20 border border-green-500/50 rounded p-2">
              {/* Check if output contains HTML (image tags) */}
              {cell.output.includes('<img') ? (
                <div 
                  className="text-xs text-engramiq-light-grey overflow-x-auto"
                  dangerouslySetInnerHTML={{ __html: cell.output }}
                />
              ) : (
                <pre className="text-xs text-engramiq-light-grey whitespace-pre-wrap overflow-x-auto">
                  {cell.output}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JupyterNotebookTab;